import {
	Box,
	Fade,
	FormControlLabel,
	Skeleton,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import { motion } from "framer-motion"
import { useMemo, useState } from "react"
import * as hooks from "lib/hooks"
import { UseMaterialColors } from "lib/hooks"
import * as partSerialStatus from "enums/partSerialStatus"
import type { StatusEntries } from "enums/partSerialStatus"
import * as sort from "enums/sort"
import { PlantsTableObj, StatusCell } from "models"
import { SectionStyle } from "lib/styles/shared"
import { GetSharedTableStyles } from "lib/styles/table"

/* ---------- animation presets (unchanged) ---------- */
const containerVariants = {
	hidden: { opacity: 0, y: 10 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			type: "spring" as const,
			stiffness: 350,
			damping: 30,
			mass: 0.8,
			when: "beforeChildren",
			staggerChildren: 0.06,
		},
	},
}
const headerVariants = {
	hidden: { opacity: 0, y: -8 },
	visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 350, damping: 30 } },
}
const fadeVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1, transition: { duration: 0.35 } },
}

export function PartSerialStatusTable({
	plants,
	loading,
	order,
	onSort,
	title,
	headerAction,
	statusEntries: statusEntriesProp,
}: {
	plants: PlantsTableObj[]
	loading: boolean
	order: sort.SortEnum
	onSort: (prop: string) => void
	title?: string
	headerAction?: React.ReactNode
	statusEntries?: StatusEntries
}) {
	const { theme } = hooks.UseTheme()
	const materialColors = UseMaterialColors()
	const [showPct, setShowPct] = useState(false)
	const styles = GetSharedTableStyles("small")

	/* ---------- header cells ---------- */
	const defaultEntries = useMemo(() => partSerialStatus.Statuses.Entries() as StatusEntries, [])
	const statusEntries = statusEntriesProp ?? defaultEntries
	const sortable = plants.length > 1

	/* ---------- helpers ---------- */
	const formatCell = (val: StatusCell) =>
		showPct ? `${val.percentage}%` : `${val.count}`

	const noData = !loading && plants.length === 0

	/* ---------- UI ---------- */
	return (
		<>
			{/* ───── header bar ───── */}
			<Box component={motion.div} variants={headerVariants} sx={SectionStyle}>
				<Typography variant="h5">{title || "Plant Status"}</Typography>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{!noData && (
						<FormControlLabel
							label={
								<Typography variant="body2">
									Show {showPct ? "Counts" : "Percentages"}
								</Typography>
							}
							labelPlacement="start"
							control={
								<Switch
									size="small"
									checked={showPct}
									disabled={loading}
									onChange={(e) => setShowPct(e.target.checked)}
								/>
							}
							sx={{ m: 0 }}
						/>
					)}
					{headerAction}
				</Box>
			</Box>

			{/* ───── empty state ───── */}
			<Fade in={noData} unmountOnExit>
				<Box component={motion.div} variants={fadeVariants} sx={SectionStyle}>
					<Typography
						data-test-selector="part-serial-status-table-no-data"
						variant="subtitle1"
						sx={{ color: materialColors.onSurfaceVariant }}
					>
						No data to display for this containment
					</Typography>
				</Box>
			</Fade>

			{/* ───── data table ───── */}
			{!noData && (
				<TableContainer sx={{ maxHeight: "calc(min(500px, 55vh))", overflow: "auto" }}>
					<Table size="small" stickyHeader sx={styles.root}>
						<TableHead>
							<TableRow>
								{/* Plant column */}
								<TableCell align="center">
									<TableSortLabel
										active={order.Data === "name"}
										direction={order.Is("Asc") ? "asc" : "desc"}
										onClick={() => onSort("name")}
										hideSortIcon={!sortable}
									>
										Plant
									</TableSortLabel>
								</TableCell>

								{/* Status columns */}
								{statusEntries.Map(([label, info]) => (
									<TableCell key={info.id} align="center">
										<TableSortLabel
											active={order.Data === info.id}
											direction={order.Is("Asc") ? "asc" : "desc"}
											onClick={() => onSort(info.id)}
											hideSortIcon={!sortable}
										>
											<Box
												display="flex"
												alignItems="center"
												gap={0.5}
												justifyContent="center"
											>
												<Tooltip title={info.definition}>
													<InfoOutlined
														sx={{
															fontSize: "1rem",
															color: materialColors.onSurfaceVariant,
														}}
													/>
												</Tooltip>
												{label}
											</Box>
										</TableSortLabel>
									</TableCell>
								))}
							</TableRow>
						</TableHead>

						<TableBody>
							{/* Loading skeleton rows */}
							{loading
								? [...Array(4)].Map((_, i) => (
										<TableRow key={`sk-${i}`}>
											<TableCell align="left">
												<Skeleton variant="text" width={80} />
											</TableCell>
											{statusEntries.Map((_, j) => (
												<TableCell key={`sk-cell-${i}-${j}`} align="center">
													<Skeleton
														variant="rectangular"
														sx={styles.cellSkeleton}
													/>
												</TableCell>
											))}
										</TableRow>
									))
								: plants.Map((plant) => (
										<TableRow key={plant.id}>
											<TableCell align="left">{plant.name}</TableCell>
											{statusEntries.Map(([_, info]) => (
												<TableCell key={info.id}>
													<Box
														data-test-selector={`plant-status-${_}`}
														sx={{ textAlign: "center", width: "100%" }}
													>
														{formatCell(plant[info.id] as StatusCell)}
													</Box>
												</TableCell>
											))}
										</TableRow>
									))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</>
	)
}
